import { ARIA, CLASS_NAMES, EVENTS, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Header module
 */
export default class Header extends MessageBus {
  /**
   * Constructor function of the Header class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers, and
   * create the event listeners.
   * @returns {object} Header A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References
   * Find all necessary DOM elements used in the view and cache them.
   * @returns {object} Header A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.headerContainer = this.element.querySelector(
      `.${CLASS_NAMES.CONTAINER}`
    );
    this.body = document.body;
    this.nav = this.element.querySelector(CLASS_NAMES.NAV);
    this.headerBranding = this.element.querySelector(
      `.${CLASS_NAMES.HEADER__BRANDING}`
    );
    this.headerSearchInput = this.element.querySelector(
      `.${CLASS_NAMES.HEADER__SEARCH_INPUT}`
    );
    this.headerSearchIcon = this.element.querySelector(
      `.${CLASS_NAMES.HEADER__SEARCH_ICON}`
    );
    this.headerHamburger = this.element.querySelector(
      `.${CLASS_NAMES.HEADER__HAMBURGER}`
    );
    this.headerMobile = this.element.querySelector(
      `.${CLASS_NAMES.HEADER__MOBILE}`
    );
    this.headerNavItems = this.element.querySelectorAll(
      `.${CLASS_NAMES.HEADER__NAV_ITEM_MOBILE}`
    );
    this.headerDonateLink = this.element.querySelector(
      `.${CLASS_NAMES.HEADER__DONATE_LINK}`
    );
    this.headerDonateLinkClose = this.element.querySelector(
      `.${CLASS_NAMES.HEADER__DONATE_LINK_CLOSE}`
    );
    this.headerDropdownButtons = this.element.querySelectorAll(
      `.${CLASS_NAMES.HEADER__DROPDOWN_ARROW}`
    );
    this.headerDonateDropdown = this.element.querySelector(
      `.${CLASS_NAMES.HEADER__DONATE_DROPDOWN_ARROW}`
    );

    return this;
  }

  /**
   * Create event handlers to enable interaction with view.
   * @returns {object} Header A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (this.headerHamburger) {
      this.headerHamburger.addEventListener(
        EVENTS.CLICK,
        this.toggleHamburgerMenu.bind(this)
      );
    }

    if (this.headerSearchIcon) {
      this.headerSearchIcon.addEventListener(
        EVENTS.CLICK,
        this.toggleSearch.bind(this)
      );
    }

    if (this.headerDonateLink) {
      this.headerDonateLink.addEventListener(
        EVENTS.CLICK,
        this.toggleDonate.bind(this)
      );
    }

    if (this.headerDonateLinkClose) {
      this.headerDonateLinkClose.addEventListener(
        EVENTS.CLICK,
        this.closeDonateTable.bind(this)
      );
    }

    if (this.headerNavItems) {
      this.headerNavItems.forEach((item) => {
        item.addEventListener(EVENTS.CLICK, this.toggleMobileNav.bind(this));
      });
    }

    if (this.headerDropdownButtons.length) {
      this.headerDropdownButtons.forEach((button) => {
        button.addEventListener(
          EVENTS.KEY_DOWN,
          this.handleDesktopDropdownKeydown.bind(this)
        );
      });
    }

    if (this.headerDonateDropdown) {
      this.headerDonateDropdown.addEventListener(
        EVENTS.KEY_DOWN,
        this.handleDesktopDropdownKeydown.bind(this)
      );
    }

    if (this.headerDonateDropdown) {
      this.headerDonateDropdown.addEventListener(
        EVENTS.CLICK,
        this.toggleDonateDropdownMobile.bind(this)
      );
    }

    this.element.addEventListener(
      EVENTS.FOCUSIN,
      this.handleHeaderFocusIn.bind(this)
    );

    document.addEventListener(EVENTS.CLICK, this.handleOutsideClick.bind(this));

    window.addEventListener(EVENTS.SCROLL, () => {
      this.element.classList.toggle(CLASS_NAMES.STICKY, window.scrollY > 0);
    });

    return this;
  }

  /**
   * Sets up event handlers specific to the Header component.
   * Currently, this is a placeholder method that can be extended
   * to include additional event handler bindings in the future.
   * @returns {object} Header A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    return this;
  }

  /**
   * Toggles the mobile hamburger menu.
   * @param {Event} event - The click event
   */
  toggleHamburgerMenu(event) {
    event.stopPropagation();
    this.headerHamburger.classList.toggle(CLASS_NAMES.HEADER__HAMBURGER_ACTIVE);
    this.headerMobile.classList.toggle(CLASS_NAMES.HEADER__MOBILE_ACTIVE);
    this.headerContainer.classList.toggle(CLASS_NAMES.ACTIVE);
  }

  /**
   * Toggles the search input field.
   * @param {Event} event - The click event
   */
  toggleSearch(event) {
    event.stopPropagation();
    this.headerSearchInput.classList.toggle(
      CLASS_NAMES.HEADER__SEARCH_INPUT_ACTIVE
    );
  }

  /**
   * Toggles the donate section.
   * @param {Event} event - The click event
   */
  toggleDonate(event) {
    event.stopPropagation();
    this.headerDonateLink.classList.toggle(
      CLASS_NAMES.HEADER__DONATE_LINK_CLICKED
    );
  }

  /**
   * Closes the donate table for mobile view.
   */
  closeDonateTable() {
    this.headerDonateFull.classList.remove(
      CLASS_NAMES.HEADER__DONATE_LINK_FULL_HOVER
    );
    this.headerDonateLink.classList.remove(
      CLASS_NAMES.HEADER__DONATE_LINK_CLICKED
    );
  }

  /**
   * Toggles navigation items for mobile menu.
   * @param {Event} event - The click event
   * @returns {object} Header A reference to the current instance of the class
   */
  toggleMobileNav(event) {
    const menuItem = event.currentTarget.nextElementSibling;
    event.currentTarget.classList.toggle(CLASS_NAMES.ACTIVE);
    if (
      menuItem.classList.contains(
        CLASS_NAMES.HEADER__NAVIGATION_CHILDREN_ACTIVE
      )
    ) {
      menuItem.classList.remove(CLASS_NAMES.HEADER__NAVIGATION_CHILDREN_ACTIVE);
    } else {
      menuItem.classList.add(CLASS_NAMES.HEADER__NAVIGATION_CHILDREN_ACTIVE);
    }
    return this;
  }

  toggleDesktopDropdown(event) {
    event.preventDefault();
    event.stopPropagation();

    const button = event.currentTarget;
    const dropdown = button.nextElementSibling;

    if (!dropdown) return;

    const isExpanded = button.getAttribute(ARIA.EXPANDED) === 'true';

    // Close all other open dropdowns (recommended)
    this.closeAllDesktopDropdowns(button);

    button.setAttribute(ARIA.EXPANDED, String(!isExpanded));
    dropdown.classList.toggle(
      CLASS_NAMES.HEADER__NAVIGATION_CHILDREN_ACTIVE,
      !isExpanded
    );
    if (!button.classList.contains(CLASS_NAMES.SEARCH)) {
      const arrow = button.querySelector(SELECTORS.SVG);
      if (arrow) {
        arrow.style.transform = !isExpanded ? 'rotate(180deg)' : 'rotate(0deg)';
        arrow.style.transition = 'transform 0.3s ease';
      }
    }
    if (button.classList.contains(CLASS_NAMES.DONATE)) {
      button.parentElement.classList.toggle(CLASS_NAMES.FOCUSED);
    }
  }

  handleDesktopDropdownKeydown(event) {
    const { key } = event;
    const button = event.currentTarget;
    const dropdown = button.nextElementSibling;

    if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      this.toggleDesktopDropdown(event);
    }

    if (key === 'Escape') {
      event.preventDefault();

      button.setAttribute(ARIA.EXPANDED, 'false');
      dropdown?.classList.remove(
        CLASS_NAMES.HEADER__NAVIGATION_CHILDREN_ACTIVE
      );

      // Reset arrow rotation
      if (!button.classList.contains(CLASS_NAMES.SEARCH)) {
        const arrow = button.querySelector(SELECTORS.SVG);
        if (arrow) arrow.style.transform = 'rotate(0deg)';
      }

      if (button.classList.contains(CLASS_NAMES.DONATE)) {
        button.parentElement.classList.remove(CLASS_NAMES.FOCUSED);
      }

      button.focus();
    }
  }

  closeAllDesktopDropdowns(currentButton = null) {
    this.headerDropdownButtons.forEach((button) => {
      if (currentButton && button === currentButton) return;

      const dropdown = button.nextElementSibling;

      button.setAttribute(ARIA.EXPANDED, 'false');
      dropdown?.classList.remove(
        CLASS_NAMES.HEADER__NAVIGATION_CHILDREN_ACTIVE
      );

      if (!button.classList.contains(CLASS_NAMES.SEARCH)) {
        const arrow = button.querySelector(SELECTORS.SVG);
        if (arrow) arrow.style.transform = 'rotate(0deg)';
      }
    });
  }

  handleOutsideClick(event) {
    if (this.element.contains(event.target)) {
      return;
    }

    this.headerDropdownButtons.forEach((button) => {
      const dropdown = button.nextElementSibling;

      button.setAttribute(ARIA.EXPANDED, 'false');
      dropdown?.classList.remove(
        CLASS_NAMES.HEADER__NAVIGATION_CHILDREN_ACTIVE
      );

      if (!button.classList.contains(CLASS_NAMES.SEARCH)) {
        const arrow = button.querySelector(SELECTORS.SVG);
        if (arrow) arrow.style.transform = 'rotate(0deg)';
      }
    });

    if (this.headerDonateDropdown) {
      const donateDropdown = this.headerDonateDropdown.nextElementSibling;
      const donateArrow = this.headerDonateDropdown.querySelector(
        SELECTORS.SVG
      );
      this.headerDonateDropdown.setAttribute(ARIA.EXPANDED, 'false');
      donateDropdown?.classList.remove(
        CLASS_NAMES.HEADER__NAVIGATION_CHILDREN_ACTIVE
      );

      if (donateArrow) donateArrow.style.transform = 'rotate(0deg)';

      if (this.headerDonateDropdown.classList.contains(CLASS_NAMES.DONATE)) {
        this.headerDonateDropdown.parentElement.classList.remove(
          CLASS_NAMES.FOCUSED
        );
      }
    }

    if (this.headerSearchInput) {
      this.headerSearchInput.classList.remove(
        CLASS_NAMES.HEADER__SEARCH_INPUT_ACTIVE
      );
      this.headerSearch?.setAttribute(ARIA.EXPANDED, 'false');
    }
  }

  toggleDonateDropdownMobile(event) {
    if (window.innerWidth > 1200) return;

    event.preventDefault();
    event.stopPropagation();

    const button = this.headerDonateDropdown;
    const dropdown = button.nextElementSibling;

    const isOpen = dropdown.classList.contains(
      CLASS_NAMES.HEADER__NAVIGATION_CHILDREN_ACTIVE
    );

    dropdown.classList.toggle(
      CLASS_NAMES.HEADER__NAVIGATION_CHILDREN_ACTIVE,
      !isOpen
    );
    button.setAttribute(ARIA.EXPANDED, String(!isOpen));
    button.parentElement.classList.toggle(CLASS_NAMES.FOCUSED, !isOpen);

    const arrow = button.querySelector(SELECTORS.SVG);
    if (arrow) {
      arrow.style.transform = !isOpen ? 'rotate(180deg)' : 'rotate(0deg)';
      arrow.style.transition = 'transform 0.3s ease';
    }
  }

  handleHeaderFocusIn(event) {
    const focusedElement = event.target;

    // If focus is inside any open dropdown, do nothing
    const isInsideOpenDropdown = focusedElement.closest(
      `.${CLASS_NAMES.HEADER__NAVIGATION_CHILDREN_ACTIVE}`
    );

    if (isInsideOpenDropdown) return;

    // Otherwise close all open desktop dropdowns
    this.closeAllDesktopDropdowns();

    // Close donate dropdown if open
    if (this.headerDonateDropdown) {
      const donateMenu = this.headerDonateDropdown.nextElementSibling;

      this.headerDonateDropdown.setAttribute(ARIA.EXPANDED, 'false');
      donateMenu?.classList.remove(
        CLASS_NAMES.HEADER__NAVIGATION_CHILDREN_ACTIVE
      );

      this.headerDonateDropdown.parentElement?.classList.remove(
        CLASS_NAMES.FOCUSED
      );
    }
  }
}
